import { SearchOutlined } from '@ant-design/icons';
import { useGetTemplates } from '@vindr/services/actions/template.action';
import { useGetStructureTemplates } from '@vindr/services/actions/templateStructuredReport.action';
import {
  ParamsGetTemplates,
  ResponseGetTemplates,
  getTemplate,
} from '@vindr/services/apis/template.api';
import {
  ParamsGetTemplateStructureReport,
  ResponseGetStructureTemplates,
  STRUCTURE_REPORT_VISIBILITY,
  StructureReport,
  getStructureTemplate,
} from '@vindr/services/apis/templateStructuredReport';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import { TEMPLATE_TYPE } from '@vindr/services/constants/report.constant';
import { filterValidValue } from '@vindr/utils/helpers/common.helper';
import { Empty as AntEmpty, Button, Input, Modal, Tabs } from 'antd';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import keycloak from '@vindr/services/keycloak';
import { Empty } from '@vindr/ui';
import Loading from '@vindr/ui/src/components/ui/Loading';
import { useGlobalState } from '../../contexts/GlobalContext';

const { Search } = Input;

const TemplateList = ({
  templates,
  onSelect,
  onSearch,
  search,
  setSearch,
  isLoading,
}: {
  templates: Template[];
  onSelect: (val: Template) => void;
  onSearch: (setSearch: string) => void;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
}) => {
  const { t } = useTranslation('Vindoc');

  return (
    <div>
      <Search
        allowClear
        autoFocus
        className="mb-3"
        enterButton={<SearchOutlined className="mb-1 text-base text-black dark:text-white" />}
        loading={isLoading}
        placeholder={t('Keyword')}
        size="small"
        style={{ width: '100%' }}
        value={search}
        onChange={(val) => setSearch(val.target.value)}
        onSearch={(value) => onSearch(value)}
      />
      <div className="flex max-h-[65vh] flex-col overflow-auto">
        {isLoading ? (
          <Loading className="m-auto" />
        ) : (
          templates.map((it, idx) => (
            <Button
              key={it?.id}
              className="w-full border-0 text-left"
              size="small"
              onClick={() => onSelect(it)}
            >
              {`${idx + 1}. ${it?.name}`}
            </Button>
          ))
        )}
      </div>
      {!templates?.length && !isLoading && <Empty image={AntEmpty.PRESENTED_IMAGE_SIMPLE} />}
    </div>
  );
};

enum TEMPLATE_TAB {
  DEFAULT = 'default',
  ORDER_INSTITUTION = 'order_institution',
  MINE = 'my_templates',
}

const SelectTemplate = ({
  visible,
  order,
  onCancel,
  onSelect,
  modal,
  is_structure_report,
}: {
  visible: boolean;
  order: any;
  is_structure_report?: boolean;
  onCancel: Parameters<typeof Modal>['0']['onCancel'];
  onSelect: (value: Template) => void;
  modal: Omit<ModalStaticFunctions, 'warn'>;
}) => {
  const { message } = useGlobalState();
  const userInfo = keycloak.getUserInfo();
  const is_super = useMemo(() => userInfo?.is_super, [userInfo]);
  const [activeTab, setActiveTab] = useState('default');

  const [searchTemplate, setSearchTemplate] = useState<
    ParamsGetTemplates | ParamsGetTemplateStructureReport
  >({});
  const {
    data: templatesResponse,
    isInitialLoading: isInitialLoadingTemplates,
    isRefetching: isRefetchingTemplates,
  } = useGetTemplates(
    {
      offset: 0,
      limit: 100,
      sort_field: 'name',
      sort_order: SORT_ORDER.ASC,
      ...(!is_super && { institution_ids: userInfo.institution_ids }),
      ...(activeTab === TEMPLATE_TAB.ORDER_INSTITUTION && {
        institution_ids: [order?.institution_id],
      }),
      ...(activeTab === TEMPLATE_TAB.MINE && {
        user_ids: [userInfo?.id],
      }),
      ...(activeTab !== TEMPLATE_TAB.MINE && {
        status: TEMPLATE_TYPE.PUBLIC,
      }),
      ...filterValidValue(
        { ...(searchTemplate as ParamsGetTemplates) },
        { invalidList: ['', null, undefined] }
      ),
    },
    { enabled: visible && !is_structure_report }
  );
  const {
    data: structureTemplatesResponse,
    isInitialLoading: isInitialLoadingStructureTemplates,
    isRefetching: isRefetchingStructureTemplates,
  } = useGetStructureTemplates(
    {
      limit: 100,
      offset: 0,
      sort_field: 'name',
      sort_order: SORT_ORDER.ASC,
      ...(!is_super && { institution_ids: userInfo.institution_ids }),
      ...(activeTab === TEMPLATE_TAB.ORDER_INSTITUTION && {
        institution_ids: [order?.institution_id],
      }),
      ...(activeTab === TEMPLATE_TAB.MINE && {
        creator_ids: [userInfo?.id],
      }),
      ...(activeTab !== TEMPLATE_TAB.MINE && {
        visibility: STRUCTURE_REPORT_VISIBILITY.PUBLIC,
      }),
      ...filterValidValue(
        { ...(searchTemplate as ParamsGetTemplateStructureReport) },
        { invalidList: ['', null, undefined] }
      ),
    },
    { enabled: visible && !!is_structure_report }
  );

  const templates = useMemo(
    () => (is_structure_report ? structureTemplatesResponse : templatesResponse)?.data || [],
    [is_structure_report, structureTemplatesResponse, templatesResponse]
  );

  const isLoading = useMemo(
    () =>
      is_structure_report
        ? isInitialLoadingStructureTemplates || isRefetchingStructureTemplates
        : isInitialLoadingTemplates || isRefetchingTemplates,
    [
      isInitialLoadingStructureTemplates,
      isInitialLoadingTemplates,
      isRefetchingStructureTemplates,
      isRefetchingTemplates,
      is_structure_report,
    ]
  );

  const { t } = useTranslation('Vindoc');

  const [search, setSearch] = useState('');

  const handleSelect = useCallback(
    async (template: Template) => {
      let templateDetail: any = template;
      if (!is_structure_report) {
        const templateDetailRes = await getTemplate(template?.id);
        templateDetail = templateDetailRes?.data;
      } else {
        const templateDetailRes = await getStructureTemplate(template?.id);
        templateDetail = templateDetailRes?.data;
      }
      if (!templateDetail) {
        message.error('Template data not found');
      } else {
        modal.confirm({
          className: 'delete-modal',
          zIndex: 1200,
          title: t('This template will overwrite your current work?'),
          okText: t('Yes'),
          cancelText: t('No'),
          onOk: () => onSelect(templateDetail),
        });
      }
    },
    [modal, onSelect, t, is_structure_report]
  );

  useEffect(() => {
    //? Clean search text when close SelectTemplate modal
    if (visible === false) {
      setSearchTemplate({});
      setSearch('');
    }
  }, [visible]);

  const onSearch = useCallback((name: string) => {
    setSearchTemplate({ name });
  }, []);

  const items = useMemo(
    () => [
      {
        key: TEMPLATE_TAB.DEFAULT,
        label: t('Default'),
      },
      {
        key: TEMPLATE_TAB.ORDER_INSTITUTION,
        label: t('Order Institution Templates'),
      },
      {
        key: TEMPLATE_TAB.MINE,
        label: t('My Templates'),
      },
    ],
    [t]
  );

  return (
    <Modal
      destroyOnClose
      forceRender
      className="vindr-modal add-order max-w-screen"
      footer={null}
      maskClosable={false}
      open={visible}
      title={t('Report Template')}
      zIndex={1001}
      onCancel={onCancel}
    >
      {visible && (
        <>
          <Tabs
            destroyInactiveTabPane
            activeKey={activeTab}
            defaultActiveKey="default"
            items={items}
            onChange={(key) => setActiveTab(key)}
          />
          <TemplateList
            templates={templates}
            onSelect={handleSelect}
            {...{ onSearch, search, setSearch, isLoading }}
          />
        </>
      )}
    </Modal>
  );
};

type Template = ResponseGetTemplates['data'][0] | ResponseGetStructureTemplates['data'][0];
// type Accumulator = { publicTemplates: Template[]; myTemplates: Template[] };

export function isStructureReport(item: Template): item is StructureReport {
  return 'visibility' in item && 'sections' in item;
}

export default SelectTemplate;
